import type { vitality as en } from '@/i18n/locales/en/vitality';
import type { Translation } from '@/i18n/types';

export const vitality: Translation<typeof en> = {
  page: {
    title: 'Vitalidad',
    description: 'Tendencias de bienestar anonimizadas e indicativas a nivel de grupo.',
    noScope: 'Selecciona una empresa para ver sus cifras de vitalidad.',
    noDepartments: 'Ningún departamento aquí tiene estadísticas que puedas ver.',
    disclaimer:
      'Todas las cifras son <strong>indicadores orientativos de bienestar</strong> y <strong>no constituyen un diagnóstico médico</strong>. Las tendencias solo se muestran cuando hay suficientes participantes (privacidad desde el diseño).',
  },
  filters: {
    title: 'Filters',
    department: {
      label: 'Departamento',
    },
    location: {
      label: 'Ubicación',
      all: 'Todas las ubicaciones',
    },
  },
  badge: {
    measured: 'Medido',
    aiDerived: 'Análisis de IA',
  },
  level: {
    good: 'Bien',
    opportunity: 'Oportunidad',
  },
  statusPill: {
    healthy: 'Nivel saludable',
    opportunity: 'Oportunidad de ayudar',
    strongForward: 'Fuerte y en ascenso',
  },
  trend: {
    flat: 'Sin cambios',
    vsPrevious: 'vs período anterior',
    vsAverage: 'vs promedio del período',
    up: 'En ascenso',
    down: 'Ligeramente más bajo',
    upBad: 'Ligeramente más alto',
    week: 'sem.',
  },
  units: {
    hoursMinutes: '{{h}}h {{m}}m',
    average: 'prom. {{value}}',
  },
  status: {
    noData: 'Sin datos en este período.',
  },
  table: {
    level: 'Nivel',
    trend: 'Tendencia',
    action: 'Acción',
    actionNone: '—',
    actionLink: 'Departamento',
    actionLinkTitle: 'Ir a este departamento',
  },
  insight: {
    title: 'Qué dicen las cifras de vitalidad',
    unavailable: 'Aún no hay suficientes datos para un resumen.',
    vitality: {
      up: 'La vitalidad va en aumento ({{pct}}% {{period}}) con una tasa de participación del {{participation}}%.',
      down: 'La vitalidad está disminuyendo ({{pct}}% {{period}}) con una tasa de participación del {{participation}}%.',
      flat: 'La vitalidad es estable ({{pct}}% {{period}}) con una tasa de participación del {{participation}}%.',
      noParticipation: 'La vitalidad es estable ({{pct}}% {{period}}).',
    },
    period: {
      last7Days: 'en los últimos 7 días',
      last30Days: 'este mes',
      thisWeek: 'esta semana',
      thisMonth: 'este mes',
      custom: 'en este período',
    },
    stress: {
      up: 'El estrés va en aumento ({{pct}}%)',
      down: 'el estrés está disminuyendo ({{pct}}%)',
      flat: 'el estrés se mantiene estable',
    },
    sleep: {
      longer: 'y los empleados duermen en promedio {{minutes}} min más.',
      shorter: 'y los empleados duermen en promedio {{minutes}} min menos.',
      same: 'y los empleados duermen aproximadamente lo mismo.',
    },
    mostActiveDay: '{{day}} es el día más activo.',
  },
  cards: {
    vitalityScore: {
      title: 'Puntuación de vitalidad',
      unit: '%',
      info: 'Una puntuación compuesta basada en el sueño, la actividad, la VFC, el estrés y la frecuencia cardíaca en reposo.',
    },
    sleep: {
      title: 'Sueño',
      unit: '',
      info: 'Duración media del sueño por noche.',
    },
    stress: {
      title: 'Estrés',
      unit: '%',
      info: 'Nivel de estrés medido; cuanto más bajo, más calmado.',
    },
    recovery: {
      title: 'Recuperación',
      unit: '%',
      info: 'Qué tan bien se recupera el cuerpo, según el sueño, la VFC y el estrés.',
    },
    activity: {
      title: 'Actividad',
      unit: 'pasos/día',
      info: 'Número medio de pasos por día.',
    },
    hrv: {
      title: 'VFC',
      unit: 'ms',
      info: 'Variabilidad de la frecuencia cardíaca — un indicador de recuperación y resiliencia.',
    },
    restingHeartRate: {
      title: 'Frecuencia cardíaca en reposo',
      unit: 'lpm',
      info: 'La frecuencia cardíaca más baja registrada en el período seleccionado, como aproximación de la frecuencia cardíaca en reposo.',
    },
  },
  actions: {
    vitalityScore: {
      good: 'La vitalidad evoluciona bien — mantén las rutinas actuales.',
      opportunity:
        'La vitalidad tiene margen de mejora — una breve charla de equipo puede revelar qué lo frena.',
    },
    sleep: {
      good: 'El sueño del equipo se ve saludable — no hace falta actuar.',
      opportunity:
        'El sueño está por debajo del objetivo — considera un recordatorio sobre la rutina nocturna y menos pantallas antes de dormir.',
    },
    stress: {
      good: 'El nivel de estrés está controlado — sigue monitorizando.',
      opportunity:
        'El estrés está elevado — una breve revisión de la carga de trabajo o una actividad de recuperación puede ayudar.',
    },
    recovery: {
      good: 'La recuperación es sólida — el equipo se recupera bien entre esfuerzos.',
      opportunity:
        'La recuperación va rezagada — revisa si los días de descanso y la carga están equilibrados.',
    },
    activity: {
      good: 'El nivel de actividad es saludable — no hace falta actuar.',
      opportunity:
        'La actividad está por debajo del objetivo — una reunión caminando o un reto de pasos puede ayudar.',
    },
    hrv: {
      good: 'La VFC se ve saludable — no hace falta actuar.',
      opportunity:
        'La VFC es más baja de lo esperado — suele relacionarse con el estrés o el mal sueño; conviene hablarlo en equipo.',
    },
    restingHeartRate: {
      good: 'La frecuencia cardíaca en reposo está en un rango saludable.',
      opportunity:
        'La frecuencia cardíaca en reposo está elevada — combinada con estrés o menos sueño, merece atención.',
    },
  },
};
